import { collection, addDoc, updateDoc, doc, getDocs, query, where, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Drone, DroneMission, MissionTemplate, FlightLog, MissionReport, FinancialRecord } from '../types';
import { handleFirestoreError, OperationType } from '../lib/firestore-errors';
import { sanitizeForFirestore } from '../lib/utils';

// Constants for collections
const MISSIONS_COLLECTION = 'droneMissions';
const DRONES_COLLECTION = 'drones';
const TEMPLATES_COLLECTION = 'missionTemplates';
const LOGS_COLLECTION = 'flightLogs';
const REPORTS_COLLECTION = 'missionReports';
const FINANCE_COLLECTION = 'financialRecords';

// Seed Templates if they don't exist
export const seedMissionTemplates = async () => {
  try {
    const templatesRef = collection(db, TEMPLATES_COLLECTION);
    const snapshot = await getDocs(templatesRef);
    
    if (snapshot.empty) {
      const defaultTemplates: Omit<MissionTemplate, 'id'>[] = [
        {
          name: 'Site Survey',
          description: 'Comprehensive aerial mapping of your property to identify property lines, structures, and overall canopy layout.',
          defaultAltitude: 200,
          requiredPayload: 'High-Res RGB Camera',
          estimatedDurationMins: 30,
          basePrice: 149,
          deliverables: ['Orthomosaic Map', 'Raw Video', 'High-Res Photos']
        },
        {
          name: 'Health Inspection',
          description: 'High-resolution multispectral imaging to detect early signs of disease, pest infestation, or nutrient deficiency.',
          defaultAltitude: 100,
          requiredPayload: 'Multispectral Sensor',
          estimatedDurationMins: 45,
          basePrice: 199,
          deliverables: ['NDVI Map', 'AI Health Report', 'High-Res Photos']
        },
        {
          name: 'Storm Assessment',
          description: 'Rapid post-storm deployment to assess structural damage, hanging limbs, and immediate hazards safely.',
          defaultAltitude: 150,
          requiredPayload: 'Thermal/RGB Dual Camera',
          estimatedDurationMins: 20,
          basePrice: 249,
          deliverables: ['Raw Video', 'Hazard Identification Report']
        },
        {
          name: 'Post-Work Verification',
          description: 'Final aerial flyover to verify completed work meets ANSI A300 standards and client expectations.',
          defaultAltitude: 100,
          requiredPayload: 'High-Res RGB Camera',
          estimatedDurationMins: 15,
          basePrice: 99,
          deliverables: ['Raw Video', 'High-Res Photos']
        }
      ];

      for (const template of defaultTemplates) {
        await addDoc(templatesRef, sanitizeForFirestore(template));
      }
    }
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, TEMPLATES_COLLECTION);
  }
};

export const requestMission = async (missionData: Omit<DroneMission, 'id'>) => {
  try {
    const docRef = await addDoc(collection(db, MISSIONS_COLLECTION), sanitizeForFirestore(missionData));
    return docRef.id;
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, MISSIONS_COLLECTION);
  }
};

export const updateMission = async (missionId: string, updates: Partial<DroneMission>) => {
  try {
    const docRef = doc(db, MISSIONS_COLLECTION, missionId);
    await updateDoc(docRef, sanitizeForFirestore(updates));
  } catch (error) {
    handleFirestoreError(error, OperationType.UPDATE, MISSIONS_COLLECTION);
  }
};

export const simulateFlight = async (mission: DroneMission, pilotId: string) => {
  try {
    // 1. Create Flight Log
    const flightLog: Omit<FlightLog, 'id'> = {
      missionId: mission.id,
      droneId: mission.droneId,
      pilotId: pilotId,
      date: new Date().toISOString(),
      durationMins: 25,
      maxAltitude: mission.flightAltitude || 150,
      batteryUsagePercent: 42,
      weatherConditions: 'Clear, 5mph winds',
      pilotNotes: 'Flight executed nominally. No interference detected.',
      telemetryDataUrl: 'https://example.com/telemetry/mock-data'
    };
    await addDoc(collection(db, LOGS_COLLECTION), sanitizeForFirestore(flightLog));

    // 2. Generate Mission Report (Mock AI Analysis)
    const report: Omit<MissionReport, 'id'> = {
      missionId: mission.id,
      jobId: mission.jobId,
      dateGenerated: new Date().toISOString(),
      aiAnalysis: `AI Analysis Complete: Canopy density is optimal. Identified 2 potential deadwood hazards in sector 4. No signs of oak wilt detected. Structural integrity of main leaders appears sound.`,
      mediaLinks: [
        'https://images.unsplash.com/photo-1508614589041-895b88991e3e?auto=format&fit=crop&q=80',
        'https://images.unsplash.com/photo-1473448912268-2022ce9509d8?auto=format&fit=crop&q=80'
      ],
      identifiedHazards: ['Deadwood overhanging driveway', 'Minor structural lean on secondary trunk'],
      recommendations: ['Schedule deadwood pruning within 3 months', 'Monitor lean annually'],
      status: 'Published'
    };
    await addDoc(collection(db, REPORTS_COLLECTION), sanitizeForFirestore(report));

    // 3. Update Mission Status
    await updateMission(mission.id, {
      status: 'Completed',
      reportGenerated: true,
      aerialMedia: report.mediaLinks,
      aiSummary: report.aiAnalysis
    });
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, 'simulateFlight');
  }
};

export const generateMissionInvoice = async (mission: DroneMission, contactId: string) => {
  try {
    const invoice: Omit<FinancialRecord, 'id'> = {
      type: 'INVOICE',
      status: 'SENT',
      contactId: contactId,
      jobId: mission.jobId,
      amount: mission.price || 0,
      date: new Date().toISOString(),
      dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days from now
      lineItems: [
        {
          id: Math.random().toString(36).substr(2, 9),
          description: `Aerial Drone Service: ${mission.missionType}`,
          quantity: 1,
          unitPrice: mission.price || 0
        }
      ],
      createdAt: new Date().toISOString()
    };
    
    await addDoc(collection(db, FINANCE_COLLECTION), sanitizeForFirestore(invoice));
    
    // We don't call updateMission here because App.tsx handles the state change
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, FINANCE_COLLECTION);
  }
};

